import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, Settings, Plus, ChevronDown, ChevronLeft, LayoutGrid, Check, HelpCircle } from 'lucide-react';
import { AYUDA } from '../config/ayuda';
import { Logo } from './Logo';
import { ModalAgregarTransaccion } from './ModalAgregarTransaccion';
import { MODULOS, moduloDeRuta, modulosPermitidos } from '../config/modulos';
import { usePerfil } from '../context/PerfilContext';
import { NOMBRE_APP } from '../config/marca';
import { AvisoVerificarEmail } from './AvisoVerificarEmail';
import { AvisoSinConexion } from './AvisoSinConexion';
import { AvisoFinRegalo } from './plus/AvisoFinRegalo';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { logout } = useAuth();
  const { tieneModulo, esAdmin, pendientesDeActivar } = usePerfil();
  const navigate = useNavigate();
  const location = useLocation();
  const [showFABModal, setShowFABModal] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const modulo = moduloDeRuta(location.pathname);
  const permitidos = modulosPermitidos(tieneModulo);
  const secciones = modulo?.secciones ?? [];
  const seccionesMovil = secciones.filter(s => s.enBottomNav !== false);

  // El FAB de transacción rápida solo tiene sentido dentro de Finanzas
  const mostrarFAB = modulo?.id === 'finanzas';

  // Cerrar el selector de módulo al cambiar de ruta o al hacer clic fuera
  useEffect(() => { setMenuAbierto(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuAbierto) return;
    const fuera = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [menuAbierto]);

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); }
    catch (e) { console.error(e); }
  };

  // Fuera de un módulo (Configuración) no hay barra inferior: sin este botón,
  // la única salida era tocar el logo, y eso no se adivina
  const conVolver = !modulo && location.pathname !== '/';
  const volver = () => {
    // React Router guarda la posición en el historial: si hay de dónde venir, atrás
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/');
  };

  const isActive = (to: string) => location.pathname === to;
  // La ayuda se abre en la sección del módulo en el que estás, si la tiene
  const enlaceAyuda = `/ayuda${modulo && AYUDA.some(s => s.id === modulo.id) ? `#${modulo.id}` : ''}`;

  const claseEnlace = (activo: boolean) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
      activo
        ? `${modulo?.acento.fondoSuave ?? 'bg-blue-50 dark:bg-blue-900/30'} ${modulo?.acento.texto ?? 'text-blue-600 dark:text-blue-400'}`
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;

  return (
    // Columna a toda altura: la barra inferior va al final, dentro de la página
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">

      {/* ── Top Navbar ─────────────────────────────────────────────────────── */}
      <nav className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-40 seguro-arriba seguro-lados">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">

          {/* Marca + selector de módulo. En pantallas estrechas (iPad en un tercio,
              iPhone pequeño) el nombre del módulo se recorta para que quepan los botones. */}
          <div className="flex items-center gap-2 min-w-0 md:shrink-0" ref={menuRef}>
            <Link to="/" className="flex items-center gap-2 shrink-0" title="Ir al inicio">
              <Logo size={34} />
              <span className="text-xl font-bold text-gray-900 dark:text-white hidden sm:inline">{NOMBRE_APP}</span>
            </Link>

            {conVolver && (
              <button onClick={volver}
                className="flex items-center gap-0.5 pl-1.5 pr-3 min-h-[44px] md:min-h-0 md:py-1.5 rounded-lg text-sm font-semibold
                           bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200
                           hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                <ChevronLeft className="w-4 h-4 shrink-0" /> Volver
              </button>
            )}

            {modulo && (
              <div className="relative min-w-0">
                <button
                  onClick={() => setMenuAbierto(v => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuAbierto}
                  aria-label={`Módulo: ${modulo.nombre}`}
                  className="flex items-center gap-1.5 px-2.5 min-h-[44px] md:min-h-0 md:py-1.5 max-w-full rounded-lg text-sm font-semibold
                             bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200
                             hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                  <modulo.Icon className={`w-4 h-4 shrink-0 ${modulo.acento.texto}`} />
                  <span className="hidden min-[360px]:inline truncate min-w-0 max-w-[110px]">{modulo.nombre}</span>
                  <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
                </button>

                {menuAbierto && (
                  <div role="menu"
                    className="absolute left-0 mt-2 w-64 rounded-xl border border-gray-200 dark:border-gray-700
                               bg-white dark:bg-gray-800 shadow-xl py-1.5 z-50">
                    <Link to="/" role="menuitem"
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
                                 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <LayoutGrid className="w-4 h-4" /> Todos los módulos
                    </Link>
                    <div className="h-px bg-gray-200 dark:bg-gray-700 my-1.5" />
                    {permitidos.map(m => (
                      <Link key={m.id} to={m.inicio} role="menuitem"
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
                                   hover:bg-gray-100 dark:hover:bg-gray-700">
                        <m.Icon className={`w-4 h-4 ${m.acento.texto}`} />
                        <span className="flex-1">{m.nombre}</span>
                        {m.id === modulo.id && <Check className="w-4 h-4 opacity-60" />}
                      </Link>
                    ))}
                    {esAdmin && MODULOS.some(m => !m.disponible) && (
                      <>
                        <div className="h-px bg-gray-200 dark:bg-gray-700 my-1.5" />
                        <p className="px-3 py-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          Próximamente
                        </p>
                        {MODULOS.filter(m => !m.disponible).map(m => (
                          <div key={m.id}
                            className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                            <m.Icon className="w-4 h-4" />
                            {m.nombre}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Nav de escritorio: secciones del módulo activo. Entre md y xl (iPad, ventanas
              medianas) solo los iconos, con el nombre al pasar el ratón: con texto no caben. */}
          <div className="hidden md:flex items-center gap-1 min-w-0">
            {secciones.map(({ to, label, Icon }) => (
              <Link key={to} to={to} title={label} aria-label={label} className={claseEnlace(isActive(to))}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="hidden xl:inline">{label}</span>
              </Link>
            ))}
            <Link to="/configuracion" title="Configuración" aria-label="Configuración"
              className={`${claseEnlace(isActive('/configuracion'))} ml-1 relative`}>
              <Settings className="w-4 h-4 shrink-0" />
              <span className="hidden xl:inline">Config.</span>
              {pendientesDeActivar > 0 && (
                <span title={`${pendientesDeActivar} cuenta(s) esperando activación`}
                  className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white
                             text-[10px] font-bold flex items-center justify-center">
                  {pendientesDeActivar}
                </span>
              )}
            </Link>
            <Link to={enlaceAyuda} title="Ayuda" aria-label="Ayuda"
              className="ml-1 p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <HelpCircle className="w-4 h-4" />
            </Link>
            <button onClick={handleLogout} title="Salir" aria-label="Salir"
              className="ml-2 flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium">
              <LogOut className="w-4 h-4" /> <span className="hidden xl:inline">Salir</span>
            </button>
          </div>

          {/* Móvil: ayuda + config + salir (el tema se elige en Configuración) */}
          <div className="md:hidden flex items-center shrink-0">
            <Link to={enlaceAyuda} aria-label="Ayuda"
              className="p-3 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
              <HelpCircle className="w-5 h-5" />
            </Link>
            <Link to="/configuracion" aria-label="Configuración"
              className={`relative p-3 rounded-lg transition ${
                isActive('/configuracion')
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}>
              <Settings className="w-5 h-5" />
              {pendientesDeActivar > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full
                                 bg-amber-500 text-white text-[10px] font-bold
                                 flex items-center justify-center">
                  {pendientesDeActivar}
                </span>
              )}
            </Link>
            <button onClick={handleLogout} aria-label="Salir"
              className="p-3 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Contenido principal ─────────────────────────────────────────────── */}
      <main className={`flex-1 seguro-lados ${seccionesMovil.length ? '' : 'seguro-abajo'}`}>
        <AvisoSinConexion />
        <AvisoVerificarEmail />
        <AvisoFinRegalo />
        {children}
      </main>

      {/* ── FAB: añadir transacción (solo en Finanzas) ──────────────────────── */}
      {mostrarFAB && (
        <button
          onClick={() => setShowFABModal(true)}
          title="Nueva transacción"
          data-sin-imprimir
          className="fixed sobre-barra-inferior right-4 md:bottom-6 md:right-6 z-30
                     w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95
                     text-white shadow-lg flex items-center justify-center transition-all">
          <Plus className="w-7 h-7" />
        </button>
      )}

      {/* ── Bottom nav (solo móvil, dentro de un módulo) ────────────────────── */}
      {/* sticky y no fixed: es lo último de la página, así que al final del
          scroll nunca tapa contenido. Con fixed, Safari de iOS (pantalla
          completa, barras que se esconden) llegaba a dejarla a media pantalla. */}
      {seccionesMovil.length > 0 && (
        <nav className="md:hidden sticky bottom-0 z-40
                        bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700
                        flex items-stretch alto-barra-inferior seguro-abajo seguro-lados">
          <Link to="/"
            className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold
                       text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition">
            <LayoutGrid className="w-5 h-5 stroke-[1.75]" />
            <span className="leading-none">Inicio</span>
          </Link>
          {seccionesMovil.map(({ to, label, Icon }) => (
            <Link key={to} to={to}
              className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition ${
                isActive(to)
                  ? modulo?.acento.texto ?? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
              <Icon className={`w-5 h-5 shrink-0 ${isActive(to) ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
              <span className="leading-none truncate max-w-full px-0.5">{label}</span>
            </Link>
          ))}
        </nav>
      )}

      {/* Modal de transacción desde el FAB */}
      {showFABModal && (
        <ModalAgregarTransaccion onClose={() => setShowFABModal(false)} />
      )}
    </div>
  );
};
